import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { FONT_WEIGHTS } from 'Theme';
import Typography from '@material-ui/core/Typography';
import { capitalize } from '@material-ui/core';

export const WEIGHTS = {
  initial: 'initial',
  ...FONT_WEIGHTS,
};

export const LINE_HEIGHTS = {
  none: 'lineHeightNone',
  small: 'lineHeightSmall',
  medium: 'lineHeightMedium',
};

export const VARIANTS = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  body: 'body1',
  fineprint: 'body2',
};

export const COLORS = {
  fdTextPrimary: 'rootColorFDTextPrimary',
  textPrimary: 'rootColorTextPrimary',
  textSecondary: 'rootColorTextSecondary',
  primary: 'rootColorPrimary',
  secondary: 'rootColorSecondary',
  accentBlue: 'rootColorAccentBlue',
  accentBlue20: 'rootColorAccentBlue20',
  black: 'rootColorBlack',
  white: 'rootColorWhite',
  purple: 'rootColorPurple',
  lightGray: 'rootColorLightGray',
  mediumGray: 'rootColorMediumGray',
  mediumDarkGray: 'rootColorMediumDarkGray',
  primaryGray: 'rootColorMediumGray',
  primaryGray4: 'rootColorPrimaryGray4',
  darkGray: 'rootColorDarkGray',
  error: 'rootColorError',
  warning: 'rootColorWarning',
  success: 'rootColorSuccess',
  seafoam: 'rootColorSeafoam',
  naturalGreen: 'rootColorNaturalGreen',
  primaryGray2: 'rootColorPrimaryGray2',
};

const reduceRootWeights = () => {
  return (acc, curr) => {
    const className = `rootFontWeight${capitalize(curr)}`;

    if (curr !== 'default') {
      acc[className] = {
        fontWeight: WEIGHTS[curr],
      };
    } else {
      acc[className] = {};
    }

    return acc;
  };
};

export const weightClasses = {
  ...Object.keys(WEIGHTS).reduce(reduceRootWeights(), {}),
};

const styles = theme => ({
  root: {},
  rootColorTextPrimary: {
    color: theme.palette.text.primary,
  },
  rootColorFDTextPrimary: {
    color: theme.palette.facet.textPrimary,
  },
  rootColorTextSecondary: {
    color: theme.palette.text.secondary,
  },
  rootColorPrimary: {
    color: theme.palette.primary.main,
  },
  rootColorSecondary: {
    color: theme.palette.secondary.main,
  },
  rootColorPurple: {
    color: theme.palette.facet.purple,
  },
  rootColorAccentBlue: {
    color: theme.palette.facet.accentBlue,
  },
  rootColorAccentBlue20: {
    color: theme.palette.facet.accentBlue20,
  },
  rootColorBlack: {
    color: theme.palette.common.black,
  },
  rootColorWhite: {
    color: theme.palette.common.white,
  },
  rootColorLightGray: {
    color: theme.palette.facet.mediumGray,
  },
  rootColorMediumGray: {
    color: theme.palette.facet.primaryGray,
  },
  rootColorMediumDarkGray: {
    color: theme.palette.facet.mediumDarkGray,
  },
  rootColorDarkGray: {
    color: theme.palette.facet.darkGray,
  },
  rootColorError: {
    color: theme.palette.error.main,
  },
  rootColorWarning: {
    color: theme.palette.warning.main,
  },
  rootColorSuccess: {
    color: theme.palette.success.main,
  },
  rootColorSeafoam: {
    color: theme.palette.facet.seafoam,
  },
  rootColorNaturalGreen: {
    color: theme.palette.facet.naturalGreen,
  },
  rootColorPrimaryGray4: {
    color: theme.palette.facet.primaryGray4,
  },
  rootColorPrimaryGray2: {
    color: theme.palette.facet.primaryGray2,
  },
  lineHeightNone: {},
  lineHeightSmall: {
    lineHeight: '24px',
  },
  lineHeightMedium: {
    lineHeight: '32px',
  },
  ...weightClasses,
});

const Text = ({
  children,
  className,
  classes,
  component,
  text,
  value,
  align,
  color,
  noWrap,
  weight,
  variant,
  test,
  title,
  lineHeight,
}) => {
  const colorClassName = COLORS[color] || COLORS.primary;
  const lineHeightClassName = LINE_HEIGHTS[lineHeight];
  const weightClassName = weight ? `rootFontWeight${capitalize(weight)}` : undefined;

  return (
    <Typography
      className={classNames(classes[colorClassName], classes[weightClassName], classes[lineHeightClassName], classes.root, className)}
      component={component}
      noWrap={noWrap}
      variant={VARIANTS[variant] || 'body1'}
      align={align}
      test={test}
      title={title}
    >
      {children || value || text}
    </Typography>
  );
};

Text.defaultProps = {
  align: 'center',
  children: null,
  className: '',
  color: 'primary',
  component: undefined,
  noWrap: false,
  weight: undefined,
  variant: 'body',
  test: undefined,
  text: '',
  value: '',
  title: undefined,
  lineHeight: 'none',
};

Text.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  component: PropTypes.string,
  /**
   * @deprecated
   */
  text: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  color: PropTypes.string,
  align: PropTypes.string,
  weight: PropTypes.oneOf(Object.keys(WEIGHTS)),
  variant: PropTypes.oneOf(Object.keys(VARIANTS)),
  test: PropTypes.string,
  noWrap: PropTypes.bool,
  title: PropTypes.string,
  lineHeight: PropTypes.string,
};

export default withStyles(styles)(Text);
